import { buildPiecePath2D } from './piecePath';
import type { RuntimePiece } from '../game/state';

/**
 * 不规则碎片的命中检测。
 *
 * 碎片有双尺寸：画板上全尺寸（scale=1）、托盘里缩略（scale=trayScale）。
 * 命中时按块所在区域用对应 scale，把指针逆变换到"全尺寸路径局部坐标"再 isPointInPath。
 */
export class HitTester {
  private readonly cache = new Map<number, Path2D>();

  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    private readonly pieceW: number,
    private readonly pieceH: number,
    private readonly boardX: number,
    private readonly boardY: number,
    private readonly boardW: number,
    private readonly boardH: number,
    private readonly trayScale: number,
  ) {}

  /** 从顶层（zIndex 高）往下，返回第一个未放置且命中的块。 */
  pick(pieces: RuntimePiece[], px: number, py: number): RuntimePiece | null {
    const sorted = pieces
      .filter((p) => !p.placed)
      .slice()
      .sort((a, b) => b.zIndex - a.zIndex);
    for (const piece of sorted) {
      const scale = this.isOnBoard(piece) ? 1 : this.trayScale;
      if (this.contains(piece, px, py, scale)) return piece;
    }
    return null;
  }

  /** 块左上角是否在画板矩形（含容差）→ 决定用全尺寸还是托盘缩略尺寸命中。 */
  private isOnBoard(piece: RuntimePiece): boolean {
    return (
      piece.currentX >= this.boardX - this.pieceW &&
      piece.currentX <= this.boardX + this.boardW &&
      piece.currentY >= this.boardY - this.pieceH &&
      piece.currentY <= this.boardY + this.boardH
    );
  }

  private pathFor(piece: RuntimePiece): Path2D {
    let p = this.cache.get(piece.id);
    if (!p) {
      p = buildPiecePath2D(piece.edges, this.pieceW, this.pieceH);
      this.cache.set(piece.id, p);
    }
    return p;
  }

  private contains(piece: RuntimePiece, px: number, py: number, scale: number): boolean {
    const path = this.pathFor(piece);
    const cx = piece.currentX + (this.pieceW * scale) / 2;
    const cy = piece.currentY + (this.pieceH * scale) / 2;
    const rad = (-piece.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = px - cx;
    const dy = py - cy;
    // 逆旋转 + 除 scale → 全尺寸路径局部坐标
    const lx = (dx * cos - dy * sin) / scale + this.pieceW / 2;
    const ly = (dx * sin + dy * cos) / scale + this.pieceH / 2;

    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    const hit = this.ctx.isPointInPath(path, lx, ly);
    this.ctx.restore();
    return hit;
  }
}
